import type { DocumentationConfigs } from '@si/types';
import { post } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { useCallback } from 'react';

import { keycloak } from '../keycloak';
import { queryKeys } from './queryKeys';

interface GenerateReportParams {
  projectId: string;
  reportId?: string | number;
  configs: DocumentationConfigs;
  filename: string;
  pdfRenderer?: 'legacy' | 'print';
  recreate?: boolean;
}

interface GenerateReportContext {
  refreshTimeout?: ReturnType<typeof setTimeout>;
}

export function useGenerateReport() {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, error, isPending, isSuccess, reset } = useMutation<
    void,
    Error,
    GenerateReportParams,
    GenerateReportContext
  >({
    onMutate: ({ projectId }) => ({
      refreshTimeout: setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: queryKeys.getReports(projectId) });
      }, 5000),
    }),
    mutationFn: ({
      projectId,
      reportId,
      configs,
      filename,
      pdfRenderer = 'print',
      recreate = false,
    }: GenerateReportParams) =>
      post({
        url: reportId
          ? `/projects/${projectId}/reports/${reportId}/${recreate ? 'recreate' : 'generate'}`
          : `/projects/${projectId}/reports/generate`,
        body: {
          configs,
          filename,
          language: i18next.language,
          pdfRenderer,
          token: keycloak.token,
        },
      }),
    onSettled: (_, __, { projectId }, context) => {
      if (context?.refreshTimeout) {
        clearTimeout(context.refreshTimeout);
      }

      void queryClient.invalidateQueries({ queryKey: queryKeys.getReports(projectId) });
    },
  });

  const generateReport = useCallback((params: GenerateReportParams) => mutate(params), [mutate]);
  const generateReportAsync = useCallback(
    (params: GenerateReportParams) => mutateAsync(params),
    [mutateAsync]
  );

  return {
    generateReport,
    generateReportAsync,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
